import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, ChevronLeft, ShieldOff } from 'lucide-react';
import { useAuth } from '@/lib/AuthContext';
import { useToast } from '@/components/ui/use-toast';
import { extensionsTotal as sumExtensions } from '@/lib/sessionExtensions';
import WalkInPos from '@/components/walkin/WalkInPos';
import WalkInDetails from '@/components/walkin/WalkInDetails';
import WalkInDisclaimer from '@/components/walkin/WalkInDisclaimer';
import WalkInPayment from '@/components/walkin/WalkInPayment';
import WalkInSuccessModal from '@/components/walkin/WalkInSuccessModal';
import { MR_SQUARE_MARK_URL } from '@/lib/brandMark';
import { useBookingConfig } from '@/lib/BookingConfigContext';

function walkInDate() {
  return new Date().toISOString().slice(0, 10);
}
function walkInSlot() {
  const h = new Date().getHours();
  const clamped = Math.min(20, Math.max(9, h));
  return `${String(clamped).padStart(2, '0')}:00`;
}

function Centered({ children }) {
  return (
    <div className="min-h-screen bg-[#0d0d0f] flex flex-col items-center justify-center px-6 text-center">
      {children}
    </div>
  );
}

export default function WalkIn() {
  const navigate = useNavigate();
  const { user, isLoadingAuth, navigateToLogin } = useAuth();
  const { toast } = useToast();
  const isAdmin = user?.role === 'admin';

  const [step, setStep] = useState(1);
  const [experience, setExperience] = useState('');
  const [rigType, setRigType] = useState('');
  const [addons, setAddons] = useState([]);
  const [addonsTotal, setAddonsTotal] = useState(0);
  // Extra time sold on bays that are already mid-session.
  const [extensions, setExtensions] = useState([]);
  const [orderAmount, setOrderAmount] = useState(0);
  const [details, setDetails] = useState({ first_name: '', last_name: '', email: '', phone: '', dob: '', postcode: '' });
  const [disclaimer, setDisclaimer] = useState(null);
  // Set once the sale is paid — drives the success / dispatch / receipt modal.
  const [payment, setPayment] = useState(null);
  // Identifies this basket for the remote payment routes, so one basket can never
  // raise two live payment requests and can never be paid twice.
  const [basketKey, setBasketKey] = useState('');

  // Access guard — admins only, mirroring the venue kiosk.
  useEffect(() => {
    if (isLoadingAuth) return;
    if (!user) {
      navigateToLogin();
    } else if (user.role !== 'admin') {
      navigate('/', { replace: true });
    }
  }, [isLoadingAuth, user, navigateToLogin, navigate]);

  const { experiences } = useBookingConfig();
  const exp = experiences[experience];
  // An extension-only sale is extra time on a rig already mid-session, so the
  // customer details and disclaimer steps are skipped and it goes to payment.
  const extensionOnly = extensions.length > 0 && !experience;
  // No session behind the sale at all — either extra time on a running rig, or a
  // straight retail basket — so there are no details or disclaimer steps behind it.
  const noSession = !experience;
  const payload = {
    experienceType: experience,
    experienceName: exp?.name || experience,
    amount: orderAmount || (exp?.price || 0),
    customerName: `${details.first_name} ${details.last_name}`.trim(),
    firstName: details.first_name,
    lastName: details.last_name,
    postcode: details.postcode,
    customerEmail: details.email,
    customerPhone: details.phone,
    date: walkInDate(),
    timeSlot: walkInSlot(),
    rigType,
    addons,
    addonsTotal,
    extensions,
    extensionsTotal: sumExtensions(extensions),
    extensionOnly,
    recordedBy: user?.full_name || user?.email || '',
    signatureUrl: disclaimer?.signatureUrl,
    emergencyName: disclaimer?.emergencyName,
    emergencyPhone: disclaimer?.emergencyPhone,
    signedAsGuardian: disclaimer?.signedAsGuardian,
  };

  const handlePosContinue = ({ experienceType, rigType: rig, addons: ad, addonsTotal: adt, extensions: exts, amount }) => {
    setExperience(experienceType);
    setRigType(rig);
    setAddons(ad);
    setAddonsTotal(adt);
    setExtensions(exts || []);
    setOrderAmount(amount);
    setBasketKey(crypto.randomUUID());
    // Extension-only sales have no new customer to check in — straight to payment.
    setStep(experienceType ? 2 : 4);
  };

  // Confirm the time added the moment payment clears, so staff see the new end
  // time on the bay rather than having to check the kiosk.
  const handlePaid = (result) => {
    setPayment(result);
    const added = result?.extensions?.extended || [];
    if (added.length > 0) {
      toast({
        title: 'Session extended',
        description: added.map((e) => `+${e.minutes} mins on ${e.bay_number || e.rig_name}`).join(' · '),
      });
    }
  };

  if (isLoadingAuth) {
    return <Centered><Loader2 className="w-8 h-8 text-primary animate-spin" /></Centered>;
  }
  if (!isAdmin) {
    return (
      <Centered>
        <ShieldOff className="w-12 h-12 text-white/10 mb-4" />
        <p className="text-white/40 text-sm font-bold tracking-widest">ACCESS DENIED</p>
      </Centered>
    );
  }
  // Clear the sale and go back to a clean POS for the next customer.
  const resetOrder = () => {
    setPayment(null);
    setStep(1);
    setExperience('');
    setRigType('');
    setAddons([]);
    setAddonsTotal(0);
    setExtensions([]);
    setOrderAmount(0);
    setDetails({ first_name: '', last_name: '', email: '', phone: '', dob: '', postcode: '' });
    setDisclaimer(null);
    setBasketKey('');
  };

  return (
    <div className="min-h-screen bg-[#0d0d0f] text-white">
      <header className="fixed top-0 left-0 right-0 z-40 bg-[#0d0d0f]/95 backdrop-blur border-b border-white/5 px-6 py-3 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <img src={MR_SQUARE_MARK_URL} alt="Motion Reality" className="h-10 w-10 shrink-0" />
          <span className="hidden sm:block w-px h-6 bg-white/10" />
          <p className="hidden sm:block font-black tracking-tight truncate">MOTION REALITY · VENUE POS &amp; DISPATCH</p>
        </div>
        {/* In-sale step control only. The POS carries no link out: each venue app is
            installed for one job, so nothing here can drop staff out of it. */}
        {step > 1 && (
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={() => setStep(noSession ? 1 : step - 1)} className="flex items-center gap-1 text-white/40 hover:text-white text-xs font-bold tracking-widest px-3 py-3.5">
              <ChevronLeft className="w-4 h-4" /> BACK
            </button>
          </div>
        )}
      </header>

      <main className={step === 1 ? 'pt-[72px]' : 'max-w-2xl mx-auto px-6 pt-24 pb-10'}>
        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
            {step === 1 && (
              <WalkInPos onContinue={handlePosContinue} />
            )}
            {step === 2 && (
              <WalkInDetails data={details} onChange={(k, v) => setDetails((d) => ({ ...d, [k]: v }))} />
            )}
            {step === 3 && (
              <WalkInDisclaimer
                prefillName={`${details.first_name} ${details.last_name}`.trim()}
                onComplete={(d) => { setDisclaimer(d); setStep(4); }}
              />
            )}
            {step === 4 && (
              <WalkInPayment payload={payload} basketKey={basketKey} onPaid={handlePaid} />
            )}
          </motion.div>
        </AnimatePresence>

        {step === 2 && (
          <button
            disabled={!(details.first_name && details.last_name && details.email && details.phone)}
            onClick={() => setStep(3)}
            className="mt-8 w-full bg-primary text-white font-black text-sm tracking-widest py-4 disabled:opacity-30"
          >
            CONTINUE
          </button>
        )}
      </main>

      {payment && (
        <WalkInSuccessModal result={payment} payload={payload} onNewSale={resetOrder} />
      )}
    </div>
  );
}